import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { salApi, consulentiApi } from '../api';
import Modal from '../components/Modal';
import CaricaDaExcel from '../components/CaricaDaExcel';
import SchedaSal from '../components/SchedaSal';
import AnteprimaDocumento from '../components/AnteprimaDocumento';
import RettificaSal from '../components/RettificaSal';
import SelettoreCommessa from '../components/SelettoreCommessa';
import { useAuth } from '../context/AuthContext';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const STILE_STATO = {
  bozza:     'bg-gray-100 text-gray-600',
  inviato:   'bg-amber-100 text-amber-700',
  approvato: 'bg-green-100 text-green-700',
  rifiutato: 'bg-red-100 text-red-700',
};

const STATO = {
  bozza:     { l: 'Bozza del consulente', c: 'bg-gray-100 text-gray-600' },
  inviato:   { l: 'Da approvare', c: 'bg-blue-100 text-blue-700' },
  approvato: { l: 'Approvato', c: 'bg-green-100 text-green-700' },
  rifiutato: { l: 'Rifiutato', c: 'bg-red-100 text-red-600' },
};

// Data locale in formato ISO: toISOString converte in UTC e a fine giornata
// farebbe slittare la data al giorno dopo.
const oggiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const salVuoto = () => ({ commessa_id: '', descrizione: '', data_sal: oggiIso(), percentuale: '' });


/**
 * Ricava le milestone dalla modalità di pagamento concordata.
 *
 * Il campo è testo libero scritto dai commerciali ("50% acconto; 50% saldo post
 * audit", "30% acconto - 50% SAL - 20% certificazione"): non è strutturato, ma
 * le percentuali si leggono, e sono quelle che servono per proporre l'importo
 * senza rifare il conto a mano. Se non se ne ricavano almeno due che stiano
 * entro il 100%, non si propone nulla: meglio nessuna scorciatoia che una
 * sbagliata.
 */
export function milestoneDaModalita(testo) {
  if (!testo) return [];
  const t = String(testo);
  const trovate = [...t.matchAll(/(\d{1,3})\s*%/g)].map(m => Number(m[1]));
  if (trovate.length < 2) return [];
  const somma = trovate.reduce((a, b) => a + b, 0);
  if (somma > 100.5) return [];
  // L'etichetta è il pezzo di testo che accompagna la percentuale, ripulito.
  // Ogni pezzo si consuma: con "50% acconto; 50% saldo" le due quote hanno la
  // stessa percentuale, e cercando ogni volta dall'inizio prenderebbero
  // entrambe l'etichetta della prima.
  const pezzi = t.split(/[;,\-–]/).map(x => x.trim()).filter(Boolean);
  const usati = new Set();
  return trovate.map((perc, i) => {
    let k = pezzi.findIndex((p, j) => !usati.has(j) && p.includes(`${perc}%`));
    if (k < 0) k = pezzi.findIndex((_, j) => !usati.has(j));
    if (k >= 0) usati.add(k);
    const pezzo = k >= 0 ? pezzi[k] : '';
    const etichetta = pezzo.replace(/\d{1,3}\s*%/, '').trim() || `quota ${i + 1}`;
    return { perc, etichetta };
  });
}

export default function GestioneSal() {
  const { user } = useAuth();
  const [sal, setSal] = useState([]);
  const [filtroStato, setFiltroStato] = useState('inviato');
  // Per collaboratore: con i rendiconti caricati da Excel in elenco compaiono
  // decine di righe tutte "nostre", e la domanda vera è sempre "cos'ha
  // rendicontato Masini questo mese".
  const [filtroConsulente, setFiltroConsulente] = useState('');
  // Ricerca libera: con decine di rendiconti, "cos'abbiamo caricato per Ipsen?"
  // non si risponde scorrendo l'elenco.
  const [cerca, setCerca] = useState('');
  const [apertoId, setApertoId] = useState(null);
  const [anteprima, setAnteprima] = useState(null);   // il SAL di cui guardare il consuntivo
  const [modal, setModal] = useState(null);   // { mode:'approva'|'rifiuta', sal }
  const [note, setNote] = useState('');
  const [descFattura, setDescFattura] = useState('');
  const [importo, setImporto] = useState('');
  const [ordineId, setOrdineId] = useState('');   // ordine del cliente da citare in fattura
  // Fattura del mese a cui accorpare: lo stesso cliente riceve un documento
  // solo invece di una fattura per rendiconto.
  const [accorpaA, setAccorpaA] = useState('');
  const [accorpabili, setAccorpabili] = useState([]);
  const [salvando, setSalvando] = useState(false);
  // Rendiconto creato dallo staff: serve anche a noi, non solo ai consulenti
  const [commesse, setCommesse] = useState([]);
  const [consulenti, setConsulenti] = useState([]);
  const [nuovo, setNuovo] = useState(null);
  const [rettifica, setRettifica] = useState(null);   // id del SAL da rettificare
  const [params, setParams] = useSearchParams();

  // Due caricamenti possono sovrapporsi (cambio filtro + creazione di un SAL):
  // senza un progressivo la risposta più vecchia, se arriva per ultima,
  // sovrascrive quella giusta e il rendiconto appena creato non compare.
  const richiesta = useRef(0);
  // Tutti i rendiconti, a prescindere dal filtro: servono i conteggi per
  // stato. Prima il numero "da approvare" si calcolava sull'elenco filtrato e
  // spariva appena si guardava un altro stato; e le bozze non si vedevano da
  // nessuna parte, anche quando erano in attesa da giorni.
  const [tutti, setTutti] = useState([]);
  const load = (stato = filtroStato, consulente = filtroConsulente) => {
    const mia = ++richiesta.current;
    // Anche i conteggi per stato seguono il collaboratore scelto: un "3 da
    // approvare" riferito a tutti, sopra un elenco filtrato su una persona,
    // è solo un numero da non credere.
    const chi = consulente ? { consulente_id: consulente } : {};
    salApi.list(chi).then(setTutti).catch(() => {});
    // "Da approvare" raccoglie più stati (inviati e bozze nostre): si chiede
    // tutto e si raggruppa qui.
    const filtroServer = { ...chi, ...(stato && stato !== 'inviato' ? { stato } : {}) };
    return salApi.list(filtroServer).then(r => {
      if (mia === richiesta.current) setSal(r);
      return r;
    });
  };
  useEffect(() => { load(); }, [filtroStato, filtroConsulente]);
  useEffect(() => { salApi.commesseAccessibili().then(setCommesse).catch(() => setCommesse([])); }, []);
  useEffect(() => { consulentiApi.list().then(setConsulenti).catch(() => setConsulenti([])); }, []);

  // Avanzamento della commessa scelta nel nuovo rendiconto: si carica appena
  // la commessa viene selezionata, non all'apertura del modale.
  const [avanz, setAvanz] = useState(null);
  useEffect(() => {
    const id = nuovo?.commessa_id;
    if (!id) { setAvanz(null); return; }
    let vivo = true;
    setAvanz('caricamento');
    salApi.avanzamento(id)
      .then(d => { if (vivo) setAvanz(d); })
      .catch(() => { if (vivo) setAvanz(null); });
    return () => { vivo = false; };
  }, [nuovo?.commessa_id]);

  // Arrivo da "+ Aggiungi SAL" dentro una commessa: il modulo si apre già
  // puntato su quella, senza far ricercare la commessa da cui si è appena usciti.
  useEffect(() => {
    if (params.get('nuovo') !== '1') return;
    setNuovo({ ...salVuoto(), commessa_id: params.get('commessa') || '' });
    setParams({}, { replace: true });   // il parametro non deve restare nell'indirizzo
  }, [params]);

  // Arrivo da "Apri e approva" in Fatturazione: il rendiconto indicato si apre
  // da solo, qualunque sia il filtro attivo.
  useEffect(() => {
    const id = params.get('apri');
    if (!id) return;
    setFiltroStato('');
    setApertoId(Number(id));
    setParams({}, { replace: true });
  }, [params]);

  const creaSal = async () => {
    if (!nuovo.commessa_id) return alert('Scegli la commessa');
    setSalvando(true);
    try {
      const creato = await salApi.create(nuovo);
      setNuovo(null);
      // Il filtro va passato esplicitamente: setFiltroStato è asincrono e la
      // load() qui sotto userebbe ancora il valore precedente, cercando il
      // nuovo rendiconto (che è in bozza) fra quelli di un altro stato.
      setFiltroStato('');
      await load('');
      setApertoId(creato.id);    // si apre subito per inserire attività e spese
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const apriApprova = async (s) => {
    setNote(''); setDescFattura(s.descrizione || 'Avanzamento lavori');
    setImporto(String(s.importo_fattura ?? '')); setOrdineId('');
    setAccorpaA(''); setAccorpabili([]);
    setModal({ mode: 'approva', sal: s });
    salApi.accorpabili(s.id).then(setAccorpabili).catch(() => setAccorpabili([]));
    // Il dettaglio porta l'importo già diviso fra consulenza e spese e lo stato
    // di avanzamento della commessa: senza, si approva alla cieca.
    try {
      const d = await salApi.get(s.id);
      setModal(m => (m && m.sal.id === s.id) ? { ...m, dettaglio: d } : m);
      if (d.proposta_fattura?.totale > 0) setImporto(String(d.proposta_fattura.totale));
      // Un ordine solo: è quello, senza farlo scegliere.
      const attivi = (d.ordini?.ordini || []).filter(o => o.stato === 'attivo');
      if (attivi.length === 1) setOrdineId(String(attivi[0].id));
    } catch { /* il pannello resta usabile con i soli dati di elenco */ }
  };
  const apriRifiuta = (s) => { setNote(''); setModal({ mode: 'rifiuta', sal: s }); };

  const riapri = async (s) => {
    if (!confirm(`Riaprire il consuntivo "${s.descrizione || s.commessa_numero}"?\n\nTornerà fra quelli da approvare e la fattura generata verrà eliminata (solo se non ancora emessa).`)) return;
    try { await salApi.riapri(s.id); load(); } catch (e) { alert(e); }
  };

  const elimina = async (s) => {
    const avviso = s.stato === 'approvato'
      ? '\n\nATTENZIONE: è approvato. Verrà eliminata anche la fattura generata, se non ancora emessa.'
      : '';
    if (!confirm(`Eliminare definitivamente il consuntivo "${s.descrizione || s.commessa_numero}"?${avviso}`)) return;
    try { await salApi.delete(s.id); setApertoId(null); load(); } catch (e) { alert(e); }
  };

  const esporta = async (s) => {
    try {
      await salApi.scaricaConsuntivo(s.id, 'pdf',
        `Consuntivo_${String(s.commessa_numero || s.id).replace(/[^\w-]+/g, '')}`);
    } catch (e) { alert(e); }
  };

  const conferma = async () => {
    setSalvando(true);
    try {
      if (modal.mode === 'approva') {
        // Coerenza con l'ordine del cliente: si avvisa PRIMA di generare la
        // fattura, quando la cosa si può ancora sistemare con il cliente.
        const attivi = (modal.dettaglio?.ordini?.ordini || []).filter(o => o.stato === 'attivo');
        const ordine = attivi.find(o => String(o.id) === String(ordineId)) || null;
        const val = Number(importo) || 0;
        if (attivi.length && !ordine &&
            !confirm('Questa commessa ha un ordine del cliente, ma la fattura non ne cita nessuno.\n\nProcedere comunque?')) {
          setSalvando(false); return;
        }
        if (ordine && ordine.residuo !== null && val > ordine.residuo + 0.01 &&
            !confirm(`L'ordine ${ordine.numero} copre ancora ${fmt(ordine.residuo)}, questa fattura vale ${fmt(val)}.\n\n`
                   + `Si sforerebbe di ${fmt(val - ordine.residuo)}. Fatturare comunque?`)) {
          setSalvando(false); return;
        }
        if (ordine?.scaduto &&
            !confirm(`L'ordine ${ordine.numero} risulta scaduto. Fatturarci sopra comunque?`)) {
          setSalvando(false); return;
        }
        await salApi.approva(modal.sal.id, {
          note_admin: note || null,
          descrizione_fattura: descFattura,
          importo_fattura: importo === '' ? undefined : importo,
          ordine_id: ordine?.id || null,
          conferma_senza_ordine: !ordine,
          conferma_oltre_ordine: true,   // l'avviso è già stato dato qui sopra
          accorpa_a: accorpaA || undefined,
        });
      } else {
        if (!note.trim()) { setSalvando(false); return alert('Indica la motivazione del rifiuto'); }
        await salApi.rifiuta(modal.sal.id, { note_admin: note });
      }
      setModal(null); setApertoId(null); load();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const conteggi = tutti.reduce((c, s) => ({ ...c, [s.stato]: (c[s.stato] || 0) + 1 }), {});
  // "Fatturati" e "Da fatturare" non sono stati del rendiconto ma dipendono
  // dalla sua fattura: si contano a parte.
  const conteggiFiltro = (v) => {
    if (v === 'fatturato') return tutti.filter(s => s.fatturato).length;
    // Approvati = approvati la cui fattura non è ancora uscita. Quelli
    // fatturati sono chiusi e stanno nel loro gruppo.
    if (v === 'approvato' || v === 'da_fatturare')
      return tutti.filter(s => s.stato === 'approvato' && !s.fatturato).length;
    return conteggi[v] || 0;
  };

  // Chi ha scritto il rendiconto decide se è in attesa di noi. Le bozze dei
  // consulenti sono ancora in mano loro; le nostre — di solito attività extra —
  // non passano da un invio: aspettano solo di essere completate e approvate.
  const daNoi = (s) => s.utente_ruolo !== 'esterno';
  const inAttesa = (s) => s.stato === 'inviato' || (s.stato === 'bozza' && daNoi(s));

  // Su una commessa a corpo il cliente paga il corrispettivo concordato, non
  // le ore: un rendiconto che porta solo ore non ha nulla da fatturargli, e il
  // lavoro svolto si legge nell'avanzamento del collaboratore. Qui resta da
  // approvare soltanto la trasferta, se c'è. Tenerli in mezzo agli avanzamenti
  // in economia fa cercare un importo che lì non esiste.
  const soloOreACorpo = (s) => s.modalita_esecuzione !== 'economia'
    && Number(s.n_al_cliente) === 0 && !(Number(s.percentuale) > 0);
  const conSpese = (s) => Number(s.n_spese_cliente) > 0;
  const haAvanzamento = (s) => !soloOreACorpo(s);
  const daApprovare = tutti.filter(s => inAttesa(s) && (haAvanzamento(s) || conSpese(s))).length;

  // Si cerca in tutto quello che si legge nella riga: commessa, cliente,
  // descrizione, chi l'ha compilato, stato, numero di fattura, importi.
  const parole = cerca.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const passa = (s2) => !parole.length || parole.every(w => [
    s2.commessa_numero, s2.commessa_titolo, s2.cliente_nome, s2.descrizione, s2.stato,
    s2.utente_nome, s2.consulente_nome, s2.fattura_numero, s2.data_sal,
    s2.importo_fattura, s2.importo_costo,
  ].filter(x => x !== null && x !== undefined).join(' ').toLowerCase().includes(w));
  const visibili = sal.filter(passa);

  const gruppi = filtroStato !== 'inviato' ? null : [
    { k: 'inviati', titolo: 'Inviati per approvazione',
      nota: 'Chiusi da chi li ha compilati: vanno verificati e approvati.',
      righe: visibili.filter(s => haAvanzamento(s) && s.stato === 'inviato') },
    { k: 'perConto', titolo: 'Caricati per conto dei collaboratori',
      nota: 'Il foglio è loro, a inserirlo siamo stati noi: non arriverà nessun invio, si controllano e si approvano qui.',
      righe: visibili.filter(s => haAvanzamento(s) && s.stato === 'bozza' && daNoi(s) && s.consulente_nome) },
    { k: 'nostri', titolo: 'Creati da Sinthema',
      nota: 'Rendiconti nostri, tipicamente attività extra: si completano e si approvano direttamente, senza invio.',
      righe: visibili.filter(s => haAvanzamento(s) && s.stato === 'bozza' && daNoi(s) && !s.consulente_nome) },
    { k: 'speseCorpo', titolo: 'A corpo: solo spese da riaddebitare',
      nota: 'Il cliente paga il corrispettivo concordato, non le ore: qui si approva la trasferta. '
        + 'Il lavoro svolto confluisce nell\'avanzamento del collaboratore.',
      // Come gli altri gruppi, solo quelli in attesa di noi: gli approvati e i
      // fatturati hanno il loro elenco e qui farebbero massa inutile.
      righe: visibili.filter(s => soloOreACorpo(s) && conSpese(s) && inAttesa(s)) },
    { k: 'stesura', titolo: 'In stesura dai consulenti', secondario: true,
      nota: 'Non ancora inviati: passano fra quelli da approvare quando il consulente li invia.',
      righe: visibili.filter(s => s.stato === 'bozza' && !daNoi(s)) },
    { k: 'traccia', titolo: 'A corpo: solo traccia del lavoro svolto', secondario: true,
      nota: 'Niente da fatturare al cliente e nessuna spesa da riaddebitare. '
        + 'Restano come registro delle ore e del compenso, e si leggono nell\'avanzamento del collaboratore.',
      righe: visibili.filter(s => soloOreACorpo(s) && !conSpese(s) && inAttesa(s)) },
  ];
  // Elenco piatto con le intestazioni di gruppo intercalate: la scheda del
  // rendiconto resta una sola, qualunque sia il gruppo.
  const voci = gruppi
    ? gruppi.filter(g => g.righe.length).flatMap(g => [
        { titolo: g.titolo, nota: g.nota, k: g.k, n: g.righe.length, secondario: g.secondario },
        ...g.righe.map(s => ({ s, secondario: g.secondario })),
      ])
    : visibili.map(s => ({ s }));
  // Nome allineato al contenuto: comprende admin e interni, non solo gli admin.
  const isStaff = user?.ruolo !== 'esterno';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title flex items-center gap-2">
          SAL — Avanzamenti e rendiconti
          {daApprovare > 0 && filtroStato !== 'inviato' && (
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{daApprovare}</span>
          )}
        </h1>
        <button className="btn-primary"
                onClick={() => setNuovo(salVuoto())}>
          + Nuovo rendiconto
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['', 'Tutti'], ['inviato', 'Da approvare'], ['approvato', 'Approvati, da fatturare'],
          ['fatturato', 'Fatturati'],
          ['rifiutato', 'Rifiutati'], ['bozza', 'Bozze']].map(([v, l]) => (
          <button key={v} onClick={() => setFiltroStato(v)}
            className={`btn-sm btn ${filtroStato === v ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
            {l}
            {v === 'inviato' && daApprovare > 0 && (
              <span className="ml-1.5 bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{daApprovare}</span>
            )}
            {v !== 'inviato' && (v ? conteggiFiltro(v) : tutti.length) > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                filtroStato === v ? 'bg-white/25' : 'bg-gray-100 text-gray-500'}`}>
                {v ? conteggiFiltro(v) : tutti.length}
              </span>
            )}
          </button>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <select className="input py-1 text-sm w-[180px]" value={filtroConsulente}
                  title="Mostra solo i rendiconti di un collaboratore"
                  onChange={e => setFiltroConsulente(e.target.value)}>
            <option value="">Tutti i collaboratori</option>
            {consulenti.map(c => (
              <option key={c.id} value={c.id}>{[c.nome, c.cognome].filter(Boolean).join(' ')}</option>
            ))}
          </select>
          <input className="input py-1 text-sm w-[260px]"
                 placeholder="Cerca commessa, cliente, consulente…"
                 value={cerca} onChange={e => setCerca(e.target.value)} />
          {cerca && (
            <>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {visibili.length} {visibili.length === 1 ? 'rendiconto' : 'rendiconti'}
              </span>
              <button className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                      onClick={() => setCerca('')}>azzera</button>
            </>
          )}
        </div>
      </div>

      {/* La ricerca lavora sull'elenco del filtro attivo: senza dirlo, "non
          trovo Ipsen" può voler dire solo che si sta guardando un altro stato. */}
      {cerca && visibili.length === 0 && (
        <div className="card text-center py-6 text-sm text-gray-500">
          Nessun rendiconto trovato per <strong>{cerca}</strong>
          {filtroStato && <> fra quelli filtrati come <em>{filtroStato}</em></>}.
          {filtroStato && (
            <button className="ml-1.5 text-brand-600 hover:underline"
                    onClick={() => setFiltroStato('')}>cerca fra tutti</button>
          )}
        </div>
      )}


      {/* Caricamento da Excel: utile anche a noi, per i fogli che i
          collaboratori mandano via mail invece di usare il portale. */}
      {isStaff && <CaricaDaExcel consulenti={consulenti} onCreati={() => load()} />}

      <div className="space-y-2">
        {voci.length === 0 && (
          <div className="card text-center text-gray-400 py-8">
            {filtroStato === 'inviato' ? 'Nessun rendiconto in attesa' : 'Nessun rendiconto'}
          </div>
        )}
        {voci.map(v => {
          if (v.titolo) return (
            <div key={`g-${v.k}`} className="pt-3 first:pt-0">
              <h2 className={`text-sm font-semibold flex items-center gap-2 ${v.secondario ? 'text-gray-500' : 'text-gray-800'}`}>
                {v.titolo}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  v.secondario ? 'bg-gray-100 text-gray-500' : 'bg-orange-500 text-white font-bold'}`}>{v.n}</span>
              </h2>
              <p className="text-xs text-gray-500">{v.nota}</p>
            </div>
          );
          const s = v.s;
          const st = s.stato === 'bozza' && daNoi(s)
            ? { l: 'Bozza Sinthema — da approvare', c: 'bg-amber-100 text-amber-800' }
            : (STATO[s.stato] || STATO.bozza);
          const margine = Number(s.importo_fattura || 0) - Number(s.importo_costo || 0);
          return (
            <div key={s.id} className={`card ${v.secondario ? 'opacity-70 hover:opacity-100 transition-opacity' : ''}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.c}`}>{st.l}</span>
                    {s.n_rettifiche > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                            title="Importi corretti dopo l'approvazione: il dettaglio mostra il prima e il dopo">
                        rettificato
                      </span>
                    )}
                    {s.n_extra > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700"
                            title="Contiene attività non previste a contratto: possono giustificare una fattura aggiuntiva">
                        +{s.n_extra} fuori contratto
                      </span>
                    )}
                    {/* Dove è finito: con la fattura emessa il rendiconto è
                        chiuso e archiviato, e il riferimento resta a vista. */}
                    {s.fatturato && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800"
                            title={`Fattura ${s.fattura_numero || 'senza numero'}${s.fattura_data ? ' del ' + fmtDate(s.fattura_data) : ''}`}>
                        fatturato{s.fattura_numero ? ` · n. ${s.fattura_numero}` : ''}
                        {s.fattura_data ? ` del ${fmtDate(s.fattura_data)}` : ''}
                        {s.fattura_pagamento ? ' · incassata' : ''}
                      </span>
                    )}
                    {s.stato === 'approvato' && !s.fatturato && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                            title="La fattura è in coda: spuntala nel riepilogo del mese quando l'avrai emessa">
                        fattura da emettere
                      </span>
                    )}
                    <Link to={`/commesse/${s.commessa_id}`} className="text-xs font-mono text-brand-600 hover:underline">
                      {s.commessa_numero}
                    </Link>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                      s.modalita_esecuzione === 'economia' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                      {s.modalita_esecuzione === 'economia' ? 'in economia' : 'a corpo'}
                    </span>
                    <span className="text-xs text-gray-400">{fmtDate(s.data_sal)}</span>
                  </div>
                  <div className="font-medium text-gray-800 mt-1">{s.descrizione || '(senza descrizione)'}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {/* Di chi è il lavoro, non chi l'ha battuto a macchina.
                        Quando non coincidono si dicono tutt'e due. */}
                    {s.consulente_nome
                      ? <>
                          <span className="font-medium text-gray-700">{s.consulente_nome}</span>
                          {s.utente_nome && s.utente_nome !== s.consulente_nome
                            && <span className="text-gray-400"> (caricato da {s.utente_nome})</span>}
                        </>
                      : Number(s.n_consulenti) > 1
                        ? <span className="font-medium text-gray-700">{s.n_consulenti} collaboratori</span>
                        : s.utente_nome}
                    {' · '}{s.cliente_nome}
                    {s.modalita_esecuzione === 'economia' ? (
                      <>
                        {Number(s.tot_giorni) > 0 && ` · ${Number(s.tot_giorni)} gg`}
                        {Number(s.tot_ore) > 0 && ` · ${Number(s.tot_ore)} ore`}
                      </>
                    ) : (s.percentuale ? ` · avanzamento ${Number(s.percentuale)}%` : '')}
                    {Number(s.tot_spese) > 0 && ` · spese ${fmt(s.tot_spese)}`}
                  </div>
                  {/* Costo e margine valgono solo per i consulenti esterni:
                      su un rendiconto interno il margine coinciderebbe con il
                      fatturabile, che è un dato falso. */}
                  <div className="text-xs mt-1">
                    {s.utente_ruolo === 'esterno' ? (
                      <>
                        <span className="text-gray-600">costo {fmt(s.importo_costo)}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="text-brand-600 font-medium">fatturabile {fmt(s.importo_fattura)}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        <span className={margine >= 0 ? 'text-green-700' : 'text-red-600'}>margine {fmt(margine)}</span>
                      </>
                    ) : (
                      <span className="text-brand-600 font-medium">fatturabile al cliente {fmt(s.importo_fattura)}</span>
                    )}
                  </div>
                  {s.note_admin && (
                    <div className={`text-xs mt-1.5 px-2 py-1 rounded ${s.stato === 'rifiutato' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                      💬 {s.note_admin}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button className="btn-secondary btn-sm" onClick={() => setApertoId(apertoId === s.id ? null : s.id)}>
                    {apertoId === s.id ? 'Chiudi' : 'Dettaglio'}
                  </button>
                  {/* Approva e rifiuta spettano al responsabile della commessa
                      (l'admin resta abilitato per sbloccare i casi fermi). */}
                  {['bozza', 'inviato', 'rifiutato'].includes(s.stato) && s.puo_approvare && (
                    <button className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold"
                            onClick={() => apriApprova(s)}>✓ Approva</button>
                  )}
                  {['bozza', 'inviato', 'rifiutato'].includes(s.stato) && !s.puo_approvare && isStaff && (
                    <span className="text-[11px] text-gray-400 text-right leading-tight px-1"
                          title={!s.responsabile_nome
                            ? 'La commessa non ha un responsabile assegnato: approva un amministratore'
                            : s.responsabile_ruolo === 'esterno'
                              ? `${s.responsabile_nome} è un consulente esterno: non può approvare importi verso il cliente`
                              : `Approva ${s.responsabile_nome}, responsabile della commessa`}>
                      {!s.responsabile_nome ? 'nessun responsabile'
                        : s.responsabile_ruolo === 'esterno' ? 'serve un admin'
                        : `approva ${s.responsabile_nome}`}
                    </span>
                  )}
                  {s.stato === 'inviato' && s.puo_approvare && (
                    <button className="btn-sm btn bg-red-100 text-red-600 hover:bg-red-200"
                            onClick={() => apriRifiuta(s)}>✕ Rifiuta</button>
                  )}
                  {s.stato === 'approvato' && isStaff && (
                    <button className="btn-secondary btn-sm" title="Annulla l'approvazione e la fattura generata"
                            onClick={() => riapri(s)}>↺ Riapri</button>
                  )}
                  {/* Fattura già uscita: non si riapre, si rettifica con una
                      nota di credito (o una fattura integrativa) per la differenza. */}
                  {s.stato === 'approvato' && s.puo_approvare && (
                    <button className="btn-secondary btn-sm"
                            title="Correggi gli importi di un SAL già fatturato: nota di credito o fattura integrativa per la differenza"
                            onClick={() => setRettifica(s.id)}>± Rettifica</button>
                  )}
                  {rettifica === s.id && (
                    <RettificaSal salId={s.id} onClose={() => setRettifica(null)}
                                  onFatto={() => { setRettifica(null); load(); }} />
                  )}
                  {/* Prima di mandarlo al cliente si guarda: scaricarlo per
                      controllarlo riempiva i download di copie quasi uguali. */}
                  {isStaff && (
                    <button className="btn-secondary btn-sm px-2" title="Guarda il consuntivo per il cliente"
                            onClick={() => setAnteprima(s)}>👁</button>
                  )}
                  {isStaff && (
                    <button className="btn-secondary btn-sm px-2" title="Scarica il consuntivo per il cliente (PDF)"
                            onClick={() => esporta(s)}>⬇</button>
                  )}
                  {isStaff && (
                    <button className="btn-danger btn-sm px-2" title="Elimina il consuntivo"
                            onClick={() => elimina(s)}>🗑</button>
                  )}
                </div>
              </div>

              {apertoId === s.id && (
                <div className="mt-4 pt-4 border-t">
                  <SchedaSal salId={s.id} soloLettura={s.stato === 'approvato'} mostraRicavo onCambiato={load} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {nuovo && (() => {
        const cm = commesse.find(c => String(c.id) === String(nuovo.commessa_id));
        const economia = cm?.modalita_esecuzione === 'economia';
        return (
          <Modal title="Nuovo rendiconto" onClose={() => setNuovo(null)} size="lg">
            <div className="space-y-3">
              <div>
                <label className="label">
                  Commessa *
                  <span className="ml-1 text-xs text-gray-400 font-normal">(cercabile per cliente)</span>
                </label>
                <SelettoreCommessa
                  commesse={commesse}
                  value={nuovo.commessa_id}
                  onChange={v => setNuovo(n => ({ ...n, commessa_id: v }))}
                  placeholder="— Scegli la commessa —"
                />
              </div>

              {cm && (
                <div className={`text-xs rounded p-2 ${economia ? 'bg-brand-50 text-brand-800' : 'bg-blue-50 text-gray-700'}`}>
                  {economia
                    ? 'Commessa in economia: dopo aver creato il rendiconto inserirai giornate/ore e spese.'
                    : 'Commessa a corpo: indica la percentuale di avanzamento raggiunta.'}
                </div>
              )}

              {/* Dove siamo arrivati: senza questo si ridichiara una percentuale
                  già raggiunta o si rendiconta due volte lo stesso lavoro.
                  Gli importi compaiono solo allo staff. */}
              {avanz === 'caricamento' && (
                <div className="text-xs text-gray-400 py-1">Carico l'avanzamento…</div>
              )}
              {avanz && avanz !== 'caricamento' && avanz.n_sal > 0 && (
                <div className="border rounded-lg p-2.5 bg-gray-50 space-y-2">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Avanzamento attuale
                    </span>
                    <span className="text-xs text-gray-500">
                      {avanz.n_sal} rendicont{avanz.n_sal === 1 ? 'o' : 'i'} già registrat{avanz.n_sal === 1 ? 'o' : 'i'}
                    </span>
                  </div>

                  {avanz.percentuale_raggiunta > 0 && (
                    <div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="bg-brand-600 h-full"
                             style={{ width: `${Math.min(100, avanz.percentuale_raggiunta)}%` }} />
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Dichiarato finora: <strong>{avanz.percentuale_raggiunta}%</strong>
                        {!economia && ' — indica quanto avanza questo rendiconto: il totale lo somma il sistema.'}
                      </div>
                    </div>
                  )}

                  {(avanz.giornate_totali > 0 || avanz.ore_totali > 0) && (
                    <div className="text-xs text-gray-600">
                      Rendicontato: <strong>{String(avanz.giornate_totali).replace('.', ',')}</strong> giornate
                      {avanz.ore_totali > 0 && <> · {String(avanz.ore_totali).replace('.', ',')} ore</>}
                    </div>
                  )}

                  {/* Solo staff: il consulente non vede gli importi concordati */}
                  {avanz.budget !== undefined && Number(avanz.budget) > 0 && (
                    <div className="flex gap-4 text-xs text-gray-500 flex-wrap border-t pt-1.5">
                      <span>budget {fmt(avanz.budget)}</span>
                      <span className="text-brand-700">emesso {fmt(avanz.fatturato)}</span>
                      <span className="text-amber-600">in coda {fmt(avanz.da_emettere)}</span>
                    </div>
                  )}

                  <div className="border-t pt-1.5 space-y-0.5 max-h-28 overflow-y-auto">
                    {avanz.sal.map(x => (
                      <div key={x.id} className="flex items-baseline gap-2 text-xs">
                        <span className="text-gray-400 w-16 shrink-0">{fmtDate(x.data_sal)}</span>
                        <span className="flex-1 truncate text-gray-700">{x.descrizione}</span>
                        {x.percentuale != null && <span className="text-gray-600 shrink-0">{Number(x.percentuale)}%</span>}
                        {x.importo_fattura != null && (
                          <span className="text-gray-500 shrink-0">{fmt(x.importo_fattura)}</span>
                        )}
                        <span className={`shrink-0 px-1.5 rounded text-[10px] ${STILE_STATO[x.stato] || 'bg-gray-100 text-gray-600'}`}>
                          {x.stato}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Data del rendiconto</label>
                  <input className="input" type="date" value={nuovo.data_sal}
                         onChange={e => setNuovo(n => ({ ...n, data_sal: e.target.value }))} />
                </div>
                {cm && !economia && (() => {
                  const gia = (avanz && avanz !== 'caricamento') ? Number(avanz.percentuale_raggiunta) || 0 : 0;
                  const q = Number(nuovo.percentuale) || 0;
                  const dopo = +(gia + q).toFixed(2);
                  const budget = (avanz && avanz !== 'caricamento') ? Number(avanz.budget) || 0 : 0;
                  return (
                    <div>
                      <label className="label">
                        Avanzamento di questo rendiconto (%)
                        <span className="ml-1 text-xs text-gray-400 font-normal">quanto avanza ORA, non il totale</span>
                      </label>
                      <input className="input" type="number" min="0" max="100" step="0.01"
                             placeholder="es. 50" value={nuovo.percentuale}
                             onChange={e => setNuovo(n => ({ ...n, percentuale: e.target.value }))} />
                      {q > 0 && (
                        <p className={`text-xs mt-1 ${dopo > 100.01 ? 'text-red-600' : 'text-gray-600'}`}>
                          Da <strong>{gia}%</strong> a <strong>{dopo}%</strong>
                          {budget > 0 && <> · importo proposto <strong>{fmt(budget * q / 100)}</strong></>}
                          {dopo > 100.01 && <> — si supera il 100%.</>}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="label">Descrizione</label>
                <input className="input" placeholder="es. Avanzamento agosto 2026"
                       value={nuovo.descrizione}
                       onChange={e => setNuovo(n => ({ ...n, descrizione: e.target.value }))} />
              </div>

              <p className="text-xs text-gray-400">
                Il rendiconto nasce in bozza a tuo nome e si apre subito: potrai inserire
                attività e spese, e da lì generare la fattura al cliente.
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="btn-secondary" onClick={() => setNuovo(null)}>Annulla</button>
              <button className="btn-primary" onClick={creaSal} disabled={salvando}>
                {salvando ? 'Creo…' : 'Crea e apri'}
              </button>
            </div>
          </Modal>
        );
      })()}

      {modal && (
        <Modal title={modal.mode === 'approva' ? '✓ Approva rendiconto' : '✕ Rifiuta rendiconto'}
               onClose={() => setModal(null)} size="md">
          <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
            <div><span className="text-gray-500">Commessa:</span> <strong>{modal.sal.commessa_numero} — {modal.sal.commessa_titolo}</strong></div>
            <div>
              <span className="text-gray-500">Di:</span>{' '}
              {modal.sal.consulente_nome || modal.sal.utente_nome}
              {modal.sal.consulente_nome && modal.sal.utente_nome
                && modal.sal.consulente_nome !== modal.sal.utente_nome
                && <span className="text-gray-400"> · caricato da {modal.sal.utente_nome}</span>}
            </div>
            <div><span className="text-gray-500">Costo per noi:</span> <strong>{fmt(modal.sal.importo_costo)}</strong></div>
          </div>

          {modal.mode === 'approva' && (() => {
            const d = modal.dettaglio;
            const p = d?.proposta_fattura;
            const budget = Number(d?.commessa_budget) || 0;
            const fatt = Number(d?.commessa_fatturato) || 0;
            const coda = Number(d?.commessa_da_emettere) || 0;
            const dopo = fatt + coda + (Number(importo) || 0);
            const sfora = budget > 0 && dopo > budget + 0.01;
            return (
            <>
              {/* Avanzamento della commessa: approvare senza sapere quanto è
                  già stato fatturato è il modo più semplice per sforare. */}
              {budget > 0 && (
                <div className="mb-3 bg-gray-50 border rounded-lg p-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Avanzamento della commessa</span>
                    <span>{Math.round(dopo / budget * 100)}% del budget dopo questa fattura</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
                    <div className="bg-brand-600 h-full" style={{ width: `${Math.min(100, fatt / budget * 100)}%` }} />
                    <div className="bg-amber-400 h-full" style={{ width: `${Math.min(100, coda / budget * 100)}%` }} />
                    <div className={`h-full ${sfora ? 'bg-red-500' : 'bg-brand-300'}`}
                         style={{ width: `${Math.min(100, (Number(importo) || 0) / budget * 100)}%` }} />
                  </div>
                  <div className="flex gap-4 text-xs text-gray-500 mt-1.5 flex-wrap">
                    <span>budget {fmt(budget)}</span>
                    <span className="text-brand-700">emesso {fmt(fatt)}</span>
                    <span className="text-amber-600">in coda {fmt(coda)}</span>
                    <span className="text-gray-700">questo SAL {fmt(Number(importo) || 0)}</span>
                  </div>
                  {sfora && (
                    <p className="text-xs text-red-600 mt-1.5">
                      Con questo importo si supera il budget di {fmt(dopo - budget)}.
                    </p>
                  )}
                </div>
              )}

              {/* Accorpamento: se il cliente ha già una fattura in coda per
                  questo mese, meglio un documento solo con il dettaglio delle
                  commesse che tre fatture lo stesso giorno. */}
              {accorpabili.length > 0 && (
                <div className="mb-3 bg-brand-50/60 border border-brand-200 rounded-lg p-3">
                  <div className="text-sm text-gray-800 font-medium">
                    Questo cliente ha già {accorpabili.length === 1 ? 'una fattura' : `${accorpabili.length} fatture`} in coda per il mese
                  </div>
                  <div className="mt-1.5 space-y-1">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="radio" className="mt-1" name="accorpa" value="" checked={accorpaA === ''}
                             onChange={() => setAccorpaA('')} />
                      <span>Emetti una <strong>fattura nuova</strong> per questo rendiconto</span>
                    </label>
                    {accorpabili.map(f => (
                      <label key={f.id} className="flex items-start gap-2 text-sm cursor-pointer">
                        <input type="radio" className="mt-1" name="accorpa" value={f.id}
                               checked={String(accorpaA) === String(f.id)}
                               onChange={() => setAccorpaA(String(f.id))} />
                        <span>
                          Accorpa a <strong>{f.commessa_numero}</strong> — {f.descrizione}
                          {' '}({fmt(f.importo)}{f.rendiconti > 1 ? `, già ${f.rendiconti} rendiconti` : ''})
                          <span className="block text-xs text-gray-500">
                            diventerebbe {fmt(Number(f.importo) + (Number(importo) || 0))}, con le commesse elencate nella descrizione
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Ordine del cliente: il numero da citare in fattura e quanto
                  resta ordinato. Dove il cliente lavora per ordini, fatturare
                  oltre significa vedersi respingere il documento. */}
              {(() => {
                const ord = d?.ordini;
                if (!ord) return null;
                const attivi = ord.ordini.filter(o => o.stato === 'attivo');
                const val = Number(importo) || 0;
                if (!attivi.length) {
                  // Nessun ordine registrato: si dice solo se ce n'è uno allegato
                  // e mai registrato, per non trasformarlo in un avviso inutile.
                  return null;
                }
                const sel = attivi.find(o => String(o.id) === String(ordineId)) || null;
                const oltre = sel && sel.residuo !== null && val > sel.residuo + 0.01;
                return (
                  <div className={`mb-3 border rounded-lg p-3 ${oltre || (sel?.scaduto) ? 'bg-red-50/70 border-red-200' : 'bg-gray-50'}`}>
                    <label className="label">Ordine del cliente</label>
                    <select className="input" value={ordineId} onChange={e => setOrdineId(e.target.value)}>
                      <option value="">— Nessun ordine —</option>
                      {attivi.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.numero}{o.importo ? ` — ordinati ${fmt(o.importo)}` : ''}
                          {o.residuo !== null ? ` · residuo ${fmt(o.residuo)}` : ''}
                        </option>
                      ))}
                    </select>
                    {sel ? (
                      <div className="text-xs mt-1.5 space-y-0.5">
                        <div className="text-gray-600">
                          Ordinati {sel.importo ? fmt(sel.importo) : '—'} · già fatturati {fmt(sel.fatturato)}
                          {sel.in_coda ? ` · in coda ${fmt(sel.in_coda)}` : ''}
                          {sel.residuo !== null && <> · <strong>residuo {fmt(sel.residuo)}</strong></>}
                          {sel.riferimento && <> · {sel.riferimento}</>}
                        </div>
                        {oltre && (
                          <p className="text-red-600">
                            Questa fattura supera l'ordine di {fmt(val - sel.residuo)}:
                            chiedi un'integrazione dell'ordine, o riduci l'importo.
                          </p>
                        )}
                        {sel.residuo !== null && !oltre && (
                          <p className="text-gray-500">Dopo questa fattura resteranno {fmt(sel.residuo - val)}.</p>
                        )}
                        {sel.scaduto && <p className="text-red-600">L'ordine risulta scaduto.</p>}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1.5">
                        Il cliente ha emesso un ordine: senza il numero in fattura può respingerla.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Milestone concordate: un clic imposta la quota invece di
                  calcolarla a mente sul budget. */}
              {(() => {
                const ms = milestoneDaModalita(d?.tipo_fatturazione);
                if (!ms.length || !budget) return null;
                return (
                  <div className="mb-3 bg-brand-50/60 border border-brand-200 rounded-lg p-2.5">
                    <div className="text-xs text-gray-600 mb-1.5">
                      Modalità concordata: <em>{d.tipo_fatturazione}</em>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {ms.map((m, i) => {
                        const q = +(budget * m.perc / 100).toFixed(2);
                        return (
                          <button key={i} className="btn-sm btn bg-white border border-brand-300 text-brand-700 hover:bg-brand-50"
                                  onClick={() => setImporto(String(q))}
                                  title={`Imposta ${m.perc}% del budget`}>
                            {m.perc}% {m.etichetta && <span className="text-gray-500">{m.etichetta}</span>} · {fmt(q)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div className="mb-3">
                <label className="label">Importo da fatturare al cliente</label>
                <input className="input" type="number" step="0.01" value={importo}
                       onChange={e => setImporto(e.target.value)} />
                {p ? (
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs">
                    <span className="text-gray-500">Proposta:</span>
                    {/* Tre parti distinte in fattura: il corrispettivo di
                        contratto, le attività aggiuntive autorizzate e le
                        spese. Le ultime due si aggiungono al budget. */}
                    <span className="px-2 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200">
                      consulenza {fmt(Number(p.consulenza) - Number(p.extra || 0))}
                    </span>
                    {Number(p.extra) > 0 && (
                      <>
                        <span className="text-gray-400">+</span>
                        <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200"
                              title="Attività fuori contratto autorizzate dal cliente: riga separata in fattura, fuori budget">
                          extra autorizzate {fmt(p.extra)}
                        </span>
                      </>
                    )}
                    <span className="text-gray-400">+</span>
                    <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                      spese {fmt(p.spese)}
                    </span>
                    <span className="text-gray-400">=</span>
                    <strong className="text-gray-800">{fmt(p.totale)}</strong>
                    {String(importo) !== String(p.totale) && (
                      <button className="text-brand-600 hover:underline"
                              onClick={() => setImporto(String(p.totale))}>ripristina</button>
                    )}
                  </div>
                ) : null}
                {/* Nessuna proposta possibile: dirlo, invece di lasciare il
                    campo vuoto e far cercare il perché. */}
                {(!p || !(p.totale > 0)) && (() => {
                  const d = modal.dettaglio;
                  const perc = Number(d?.percentuale) || Number(modal.sal.percentuale) || 0;
                  const senzaBudget = !(Number(d?.commessa_budget) > 0);
                  if (!perc || !senzaBudget) return null;
                  return (
                    <p className="text-xs text-amber-700 mt-1.5">
                      Il rendiconto dichiara il {perc}% di avanzamento, ma la commessa non ha un budget:
                      senza quello non si può calcolare la quota. Scrivi il valore della commessa nella
                      sua scheda e la proposta arriva da sola.
                    </p>
                  );
                })()}
                {p && (
                  <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                    <p>
                      {p.origine === 'percentuale'
                        ? `Proposto dall'avanzamento dichiarato: ${p.percentuale}% del budget della commessa`
                          + (p.quota_contratto != null ? ` (${fmt(p.quota_contratto)}).` : '.')
                        : p.origine === 'righe'
                          ? 'Proposto sommando le attività a tariffa cliente e le spese riaddebitabili.'
                          : 'Proposto dall\'importo indicato sul rendiconto.'}
                    </p>
                    {/* Il lavoro fuori contratto è la ragione per cui questo
                        importo supera la quota concordata: va detto qui. */}
                    {p.n_extra > 0 && (p.extra_autorizzato ? (
                      <p className="text-purple-700">
                        Comprende {p.n_extra} attività non previste a contratto per {fmt(p.extra)},
                        autorizzate dal cliente.
                        {p.extra_da_valorizzare > 0 && (
                          <strong> {p.extra_da_valorizzare} senza importo: vanno valorizzate a mano.</strong>
                        )}
                      </p>
                    ) : (
                      <p className="text-amber-700">
                        <strong>
                          {p.n_extra} attività non previste a contratto
                          {p.extra_totale > 0 ? ` (${fmt(p.extra_totale)})` : ''} non sono comprese:
                        </strong>{' '}
                        il cliente non le ha ancora autorizzate. L'autorizzazione si registra
                        nel dettaglio del rendiconto.
                      </p>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">Lascia vuoto per non generare la fattura.</p>
              </div>
              <div className="mb-3">
                <label className="label">Descrizione sulla fattura</label>
                <input className="input" value={descFattura} onChange={e => setDescFattura(e.target.value)} />
                <p className="text-xs text-green-600 mt-1">
                  ✓ Verranno create in Fatturazione due voci "da emettere" —
                  consulenza e spese riaddebitate — così la fattura le distingue.
                </p>
              </div>
            </>
            );
          })()}

          <div>
            <label className="label">{modal.mode === 'approva' ? 'Note per il consulente (facoltative)' : 'Motivazione del rifiuto *'}</label>
            <textarea className="input" rows={2} value={note} onChange={e => setNote(e.target.value)}
                      placeholder={modal.mode === 'rifiuta' ? 'Spiega cosa va corretto…' : ''} />
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className={modal.mode === 'approva' ? 'btn-primary' : 'btn-danger'}
                    onClick={conferma} disabled={salvando}>
              {salvando ? '…' : modal.mode === 'approva' ? '✓ Approva e prepara fattura' : '✕ Conferma rifiuto'}
            </button>
          </div>
        </Modal>
      )}

      {anteprima && (
        <AnteprimaDocumento
          titolo={`Consuntivo ${anteprima.commessa_numero || ''} — ${anteprima.cliente_nome || ''}`}
          carica={() => salApi.anteprimaConsuntivo(anteprima.id)}
          onChiudi={() => setAnteprima(null)}
          onScarica={() => esporta(anteprima)}
        />
      )}
    </div>
  );
}
